/**
 * Sync problems (phone commands the server did not apply):
 *   /sync/problems              GET ?deviceId&status=OPEN|RESOLVED|DISMISSED|ALL&result&page&limit
 *   /sync/problems/:id/dismiss  POST { reason }
 *   /sync/devices               GET → { openProblems, devices: [{ deviceId, lastCommandAt, openProblems, waitingProblems }] }
 * Permission sync:problems (central admin, supervisor).
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { useCan } from '@/auth/context'
import { getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { arr, iso, num, obj, str, strOrNull } from './mappers'

export const SYNC_PROBLEMS_PERM = 'sync:problems'
const syncKey = ['console', 'sync'] as const

export type SyncProblemStatus = 'OPEN' | 'RESOLVED' | 'DISMISSED'

export interface SyncProblemRow {
  id: string
  commandId: string
  commandType: string | null
  deviceId: string
  deviceCode: string | null
  deviceName: string | null
  actorId: string | null
  actorName: string | null
  sessionId: string | null
  occurredAt: string | null
  result: string
  errorCode: string
  errorMessage: string | null
  waitingOn: string[]
  payloadSummary: Record<string, unknown> | null
  attempts: number
  firstSeenAt: string | null
  lastSeenAt: string | null
  status: SyncProblemStatus
  resolvedAt: string | null
  resolution: string | null
  dismissedAt: string | null
  dismissedByName: string | null
  dismissReason: string | null
}

export const mapSyncProblem = (r: Record<string, unknown>): SyncProblemRow => {
  const device = obj(r.device)
  const actor = obj(r.actor)
  const summary = r.payloadSummary
  return {
    id: str(r.id),
    commandId: str(r.commandId),
    commandType: strOrNull(r.commandType),
    deviceId: str(device.id),
    deviceCode: strOrNull(device.codePrefix),
    deviceName: strOrNull(device.name),
    actorId: strOrNull(actor.id),
    actorName: strOrNull(actor.name),
    sessionId: strOrNull(r.sessionId),
    occurredAt: iso(r.occurredAt),
    result: str(r.result),
    errorCode: str(r.errorCode),
    errorMessage: strOrNull(r.errorMessage),
    waitingOn: arr<string>(r.waitingOn).map(String),
    payloadSummary: summary && typeof summary === 'object' ? obj(summary) : null,
    attempts: num(r.attempts, 1),
    firstSeenAt: iso(r.firstSeenAt),
    lastSeenAt: iso(r.lastSeenAt),
    status: str(r.status, 'OPEN') as SyncProblemStatus,
    resolvedAt: iso(r.resolvedAt),
    resolution: strOrNull(r.resolution),
    dismissedAt: iso(r.dismissedAt),
    dismissedByName: strOrNull(obj(r.dismissedBy).name),
    dismissReason: strOrNull(r.dismissReason),
  }
}

export function useSyncProblems(
  f: PageParams & { deviceId?: string; status?: SyncProblemStatus | 'ALL'; result?: string },
) {
  return usePageQuery([...syncKey, 'problems', f], () =>
    getPage('/sync/problems', f, mapSyncProblem),
  )
}

export function useDismissSyncProblem() {
  return useConsoleMutation<{ id: string; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/sync/problems/${id}/dismiss`, { reason }),
    invalidate: [syncKey],
    success: 'Problem dismissed',
    errorTitle: 'Not dismissed',
  })
}

export interface DeviceSyncHealth {
  deviceId: string
  lastCommandAt: string | null
  openProblems: number
  waitingProblems: number
}

/** Per-phone last sync and open problems; only fetched for people who may see sync problems. */
export function useDeviceSyncHealth() {
  const can = useCan()
  return useQuery({
    queryKey: [...syncKey, 'devices'],
    enabled: can(SYNC_PROBLEMS_PERM),
    queryFn: async () => {
      const data = obj(await api.get<unknown>('/sync/devices'))
      const devices = arr(data.devices).map((d) => {
        const o = obj(d)
        return {
          deviceId: str(o.deviceId),
          lastCommandAt: iso(o.lastCommandAt),
          openProblems: num(o.openProblems),
          waitingProblems: num(o.waitingProblems),
        } satisfies DeviceSyncHealth
      })
      return {
        openProblems: num(data.openProblems),
        byDevice: new Map(devices.map((d) => [d.deviceId, d])),
      }
    },
  })
}
